import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useBootstrap, useTasaDia } from '../api/queries';
import {
  useBancos,
  useCobranzasFin,
  useCrearCobranza,
  useFacturasPendientes,
} from '../api/queries-finanzas';
import { FinanceDemoNotice } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { SearchSelect } from '../components/SearchSelect';
import { StatusBadge } from '../components/Badges';
import { PlusIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { formatDate, formatMoney, todayIso } from '../lib/format';

const today = todayIso();

export function CobranzasPage() {
  const { user, demo } = useAuth();
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const { notify } = useToast();

  const cobranzas = useCobranzasFin({
    search: search || undefined,
    estado: estado || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
    page,
    limit: 25,
  });
  const rows = cobranzas.data?.data ?? [];
  const canCreate =
    !demo && user !== null && ['admin', 'supervisor', 'ventas', 'cobranza'].includes(user.rol);
  const totalRecibido = rows
    .filter((row) => row.estado === 'registrada')
    .reduce((sum, row) => sum + row.montoVes, 0);
  const totalIslr = rows
    .filter((row) => row.estado === 'registrada')
    .reduce((sum, row) => sum + row.retencionIslrUsd * row.tasa, 0);

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Cobranzas y cartera</p>
          <h1 className="page-title">Cobranzas & Recibos</h1>
          <p className="page-sub">
            Aplicación FIFO a facturas, retenciones ISLR, saldo a favor y asiento automático.
          </p>
        </div>
        <button type="button" className="btn btn--primary" disabled={!canCreate} onClick={() => setOpen(true)}>
          <PlusIcon size={17} /> Registrar cobranza
        </button>
      </header>

      <FinanceDemoNotice demo={demo} />

      <div className="summary-strip">
        <div className="summary-tile">
          <span>Recibido (página)</span>
          <strong>{formatMoney(totalRecibido, 'VES')}</strong>
          <small>{rows.length} recibos</small>
        </div>
        <div className="summary-tile summary-tile--accent">
          <span>Retención ISLR</span>
          <strong>{formatMoney(totalIslr, 'VES')}</strong>
          <small>Comprobantes de clientes</small>
        </div>
        <div className="summary-tile summary-tile--warning">
          <span>Saldo a favor aplicado</span>
          <strong>{formatMoney(rows.reduce((sum, row) => sum + Math.max(0, row.saldoFavorUsadoUsd) * row.tasa, 0), 'VES')}</strong>
          <small>Saldo a favor aplicado</small>
        </div>
      </div>

      <section className="card transactions-card">
        <div className="transactions-filters">
          <div className="filter-tabs">
            {[
              ['', 'Todas'],
              ['registrada', 'Registradas'],
              ['anulada', 'Anuladas'],
            ].map(([value, label]) => (
              <button
                key={value || 'todas'}
                type="button"
                className={estado === value ? 'is-active' : undefined}
                onClick={() => {
                  setEstado(value ?? '');
                  setPage(1);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="filter-controls">
            <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
            <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
            <div className="search-input search-input--sm">
              <input
                type="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Recibo, cliente, referencia"
                aria-label="Buscar cobranzas"
              />
            </div>
          </div>
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Recibo</th>
                <th>Cliente / RIF</th>
                <th>Fecha</th>
                <th>Método</th>
                <th>Monto</th>
                <th>ISLR</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {cobranzas.isLoading ? (
                <tr><td colSpan={7}><div className="table-empty">Cargando recibos…</div></td></tr>
              ) : null}
              {!cobranzas.isLoading && rows.length === 0 ? (
                <tr><td colSpan={7}><div className="table-empty">Sin cobranzas en este rango.</div></td></tr>
              ) : null}
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.numero}</strong>
                      <span>{row.referencia ?? row.banco ?? 'Sin referencia'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.clienteNombre}</strong>
                      <span>{row.clienteRif}</span>
                    </div>
                  </td>
                  <td>{formatDate(row.cobradoAt)}</td>
                  <td>{row.metodo.replaceAll('_', ' ')}</td>
                  <td className="align-right">
                    <div className="amount-cell">
                      <strong>{formatMoney(row.montoVes, 'VES')}</strong>
                      <span>{formatMoney(row.montoVes, 'VES')}</span>
                    </div>
                  </td>
                  <td className="align-right">{formatMoney(row.retencionIslrUsd * row.tasa, 'VES')}</td>
                  <td><StatusBadge estado={row.estado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="table-footer">
          <span>
            Página {page} de {cobranzas.data?.meta.pages ?? 1} · {cobranzas.data?.meta.total ?? 0} recibos
          </span>
          <div className="pager">
            <button type="button" className="btn btn--outline btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <button type="button" className="btn btn--outline btn--sm" disabled={page >= (cobranzas.data?.meta.pages ?? 1)} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
          </div>
        </footer>
      </section>

      {open ? (
        <CobranzaForm
          onClose={() => setOpen(false)}
          onCreated={(numero) => {
            setOpen(false);
            notify(`Cobranza ${numero} registrada y contabilizada.`, 'success');
          }}
        />
      ) : null}
    </div>
  );
}

function CobranzaForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (numero: string) => void;
}) {
  const { data: bootstrap } = useBootstrap();
  const { data: bancos } = useBancos();
  const { notify } = useToast();
  const crear = useCrearCobranza();
  const [clienteId, setClienteId] = useState('');
  const [fecha, setFecha] = useState(today);
  const moneda = 'VES' as const;
  const [tasa, setTasa] = useState(String(bootstrap?.tasas.find((t) => t.moneda === 'USD')?.tasa ?? 36.42));
  const tasaDia = useTasaDia(fecha);

  useEffect(() => {
    const rate = tasaDia.data?.data.find((item) => item.moneda === 'USD');
    if (rate) setTasa(String(rate.tasa));
  }, [tasaDia.data]);
  const [bancoId, setBancoId] = useState('');
  const [metodo, setMetodo] = useState('transferencia');
  const [referencia, setReferencia] = useState('');
  const [monto, setMonto] = useState('');
  const [aplicaciones, setAplicaciones] = useState<
    Record<string, { aplicar: boolean; monto: string; islr: string; pct: string }>
  >({});

  const pendientes = useFacturasPendientes(clienteId || undefined);
  const invoices = pendientes.data ?? [];
  const selectedCliente = bootstrap?.clientes.find((cliente) => cliente.id === clienteId);

  const appliedTotal = useMemo(
    () =>
      Object.values(aplicaciones)
        .filter((item) => item.aplicar)
        .reduce((sum, item) => sum + (Number(item.monto) || 0), 0),
    [aplicaciones],
  );
  const islrTotal = useMemo(
    () =>
      Object.values(aplicaciones)
        .filter((item) => item.aplicar)
        .reduce((sum, item) => sum + (Number(item.islr) || 0), 0),
    [aplicaciones],
  );
  const montoNumber = Number(monto) || appliedTotal;

  // La retención de ISLR se calcula sobre la base imponible (sin IVA) de la factura.
  const baseIslr = (invoice: { saldoVes: number; subtotalVes: number; totalVes: number }) =>
    invoice.totalVes > 0
      ? Math.round(((invoice.saldoVes * invoice.subtotalVes) / invoice.totalVes) * 100) / 100
      : invoice.saldoVes;
  const redondearBs = (value: number) => Math.round(value * 100) / 100;

  const toggleInvoice = (
    invoiceId: string,
    saldoVes: number,
    baseVes: number,
  ) => {
    setAplicaciones((current) => {
      const existing = current[invoiceId];
      if (existing?.aplicar) {
        return { ...current, [invoiceId]: { ...existing, aplicar: false } };
      }
      const pct = existing?.pct ?? String(selectedCliente?.islrPct ?? 0);
      const islr = redondearBs((baseVes * (Number(pct) || 0)) / 100);
      return {
        ...current,
        [invoiceId]: {
          aplicar: true,
          pct,
          islr: islr > 0 ? String(islr) : '',
          monto: String(redondearBs(Math.max(0, saldoVes - islr))),
        },
      };
    });
  };

  const submit = async () => {
    if (!clienteId) {
      notify('Selecciona un cliente.', 'warning');
      return;
    }
    const selected = invoices
      .filter((invoice) => aplicaciones[invoice.id]?.aplicar)
      .map((invoice) => ({
        facturaId: invoice.id,
        montoAplicadoUsd: (Number(aplicaciones[invoice.id]?.monto) || 0) / invoice.tasa,
        retencionIslrUsd: (Number(aplicaciones[invoice.id]?.islr) || 0) / invoice.tasa,
        retencionIslrPct: Number(aplicaciones[invoice.id]?.pct) || 0,
      }));
    if (selected.length === 0) {
      notify('Selecciona al menos una factura pendiente.', 'warning');
      return;
    }
    if (montoNumber > 0 && !bancoId) {
      notify('Selecciona la cuenta bancaria o caja donde entra el dinero.', 'warning');
      return;
    }
    try {
      const result = await crear.mutateAsync({
        clienteId,
        fecha,
        moneda,
        monto: montoNumber,
        tasa: Number(tasa),
        ...(bancoId ? { cuentaBancariaId: bancoId } : {}),
        metodo,
        ...(referencia ? { referencia } : {}),
        idempotencyKey: crypto.randomUUID(),
        aplicaciones: selected,
      });
      onCreated(String((result.data as { numero?: string }).numero ?? ''));
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo registrar la cobranza.', 'error');
    }
  };

  return (
    <Modal
      title="Registrar cobranza"
      eyebrow="Recibos"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={crear.isPending}>
            {crear.isPending ? 'Registrando…' : 'Registrar y contabilizar'}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="form-grid form-grid--3">
          <label className="field">
            <span className="field-label">Cliente *</span>
            <SearchSelect
              value={clienteId}
              onChange={(value) => {
                setClienteId(value);
                setAplicaciones({});
              }}
              options={(bootstrap?.clientes ?? []).map((cliente) => ({
                value: cliente.id,
                label: cliente.nombre,
                hint: cliente.rif,
              }))}
              placeholder="Buscar cliente por nombre o RIF"
              ariaLabel="Cliente"
            />
            {selectedCliente ? (
              <span className="field-hint">
                Saldo a favor: {formatMoney(selectedCliente.saldoFavorUsd ?? 0, 'USD')}
                {selectedCliente.islrPct
                  ? ` · retiene ISLR ${selectedCliente.islrPct}%`
                  : ''}
              </span>
            ) : null}
          </label>
          <label className="field">
            <span className="field-label">Fecha</span>
            <input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <div className="field">
            <span className="field-label">Moneda</span>
            <span className="field-static">Bolívares (Bs.)</span>
          </div>
          <label className="field">
            <span className="field-label">Tasa VES/USD</span>
            <input className="input" value={tasa} onChange={(e) => setTasa(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Cuenta bancaria</span>
            <SearchSelect
              value={bancoId}
              onChange={setBancoId}
              options={[
                { value: '', label: 'Caja / sin banco' },
                ...(bancos ?? []).map((banco) => ({
                  value: banco.id,
                  label: banco.banco,
                  hint: `${banco.moneda} · ${formatMoney(banco.saldoActual, banco.moneda)}`,
                })),
              ]}
              placeholder="Buscar cuenta bancaria"
              ariaLabel="Cuenta bancaria"
            />
          </label>
          <label className="field">
            <span className="field-label">Método</span>
            <select className="input" value={metodo} onChange={(e) => setMetodo(e.target.value)}>
              <option value="transferencia">Transferencia</option>
              <option value="efectivo_usd">Efectivo USD</option>
              <option value="efectivo_ves">Efectivo VES</option>
              <option value="pago_movil">Pago móvil</option>
              <option value="zelle">Zelle</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Referencia</span>
            <input className="input" value={referencia} onChange={(e) => setReferencia(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Monto recibido (Bs.)</span>
            <input className="input" inputMode="decimal" value={monto} placeholder={String(appliedTotal)} onChange={(e) => setMonto(e.target.value)} />
          </label>
        </div>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Facturas pendientes</strong>
            <span className="pill pill--soft">{invoices.length} documentos</span>
          </div>
          {!clienteId ? <p className="muted">Selecciona un cliente para ver su cartera.</p> : null}
          {clienteId && invoices.length === 0 ? <p className="muted">El cliente no tiene facturas pendientes.</p> : null}
          <div className="allocation-list">
            {invoices.map((invoice) => {
              const base = baseIslr(invoice);
              const state = aplicaciones[invoice.id] ?? {
                aplicar: false,
                monto: String(invoice.saldoVes),
                islr: '',
                pct: String(selectedCliente?.islrPct ?? 0),
              };
              return (
                <div className={`allocation-row${state.aplicar ? ' allocation-row--active' : ''}`} key={invoice.id}>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={state.aplicar}
                      onChange={() => toggleInvoice(invoice.id, invoice.saldoVes, base)}
                    />
                    <span>
                      <strong>{invoice.numero}</strong>
                      <small>
                        {formatDate(invoice.fechaEmision)} · saldo {formatMoney(invoice.saldoVes, 'VES')}
                        {' · '}base ISLR {formatMoney(base, 'VES')}
                      </small>
                    </span>
                  </label>
                  <input
                    className="input allocation-row__amount"
                    inputMode="decimal"
                    value={state.monto}
                    disabled={!state.aplicar}
                    onChange={(e) =>
                      setAplicaciones((current) => ({ ...current, [invoice.id]: { ...state, monto: e.target.value } }))
                    }
                    aria-label={`Monto a aplicar a ${invoice.numero}`}
                  />
                  <div className="allocation-row__islr">
                    <input
                      className="input allocation-row__pct"
                      inputMode="decimal"
                      placeholder="%"
                      value={state.pct}
                      disabled={!state.aplicar}
                      onChange={(e) => {
                        const pct = e.target.value;
                        const islr = redondearBs((base * (Number(pct) || 0)) / 100);
                        const applied = Math.max(0, invoice.saldoVes - islr);
                        setAplicaciones((current) => ({
                          ...current,
                          [invoice.id]: {
                            ...state,
                            pct,
                            islr: islr > 0 ? String(islr) : '',
                            monto: String(redondearBs(applied)),
                          },
                        }));
                      }}
                      aria-label={`Porcentaje ISLR de ${invoice.numero}`}
                    />
                    <span className="allocation-row__pct-sign" aria-hidden="true">%</span>
                    <input
                      className="input allocation-row__amount"
                      inputMode="decimal"
                      placeholder="ISLR Bs."
                      value={state.islr}
                      disabled={!state.aplicar}
                      onChange={(e) => {
                        const islr = Number(e.target.value) || 0;
                        const applied = Math.max(0, invoice.saldoVes - islr);
                        const pct = base > 0 ? Math.round((islr / base) * 10000) / 100 : 0;
                        setAplicaciones((current) => ({
                          ...current,
                          [invoice.id]: {
                            ...state,
                            islr: e.target.value,
                            pct: String(pct),
                            monto: String(redondearBs(applied)),
                          },
                        }));
                      }}
                      aria-label={`Retención ISLR de ${invoice.numero}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="line-items__totals">
            <span>Aplicado {formatMoney(appliedTotal, 'VES')}</span>
            <span>ISLR {formatMoney(islrTotal, 'VES')}</span>
            <strong>Recibido {formatMoney(montoNumber, 'VES')}</strong>
          </div>
          <p className="page-footnote">
            El ISLR se calcula sobre la base imponible (sin IVA) y se puede ajustar por
            porcentaje o por monto; el porcentaje usado queda guardado en la aplicación.
            El sobrepago se acumula como saldo a favor del cliente y se contabiliza en
            Anticipos de Clientes. La retención ISLR se registra como cuenta por cobrar al
            fisco y el comprobante se asigna automáticamente desde el talonario.
          </p>
        </div>
      </div>
    </Modal>
  );
}
